import config from 'src/config';

const PWA_FLAG = 'odeko:pwa-install';
// processPathSwitchPublicPath in src/components/Navbar. Not imported: that
// module pulls in the whole GraphQL client.
const SCANNER_PATH = '/scanner';

const isStandaloneDisplay = (): boolean =>
  window.matchMedia?.('(display-mode: standalone)').matches === true ||
  (window.navigator as { standalone?: boolean }).standalone === true;

// Prod sets SERVICE_ENV to 'prod-1' (helm-charts/charts/prod/logistics-web-app).
export const isProductionEnvironment = (): boolean =>
  ['prod', 'prod-1'].includes(config().environment);

const isScannerPage = (pathname: string): boolean =>
  pathname.startsWith(SCANNER_PATH);

// Chrome installs whichever app the page's manifest describes, and iOS labels
// the home-screen icon with the apple-* tags. /scanner gets its own Scanner app
// that opens at /scanner, so adding it to a handheld's home screen doesn't
// install Putaway (which asks for a DMP login). Every other page installs
// Putaway. nginx already serves each page with the right tags
// (scripts/pwa-html.js, which Safari needs); this keeps them right as the app
// navigates. Keep these paths in sync with the files in public/.
export const applyPwaManifest = (pathname: string): void => {
  const scanner = isScannerPage(pathname);
  const prod = isProductionEnvironment();
  const set = (selector: string, attr: string, value: string): void => {
    document.head.querySelector(selector)?.setAttribute(attr, value);
  };
  set(
    'link[rel="manifest"]',
    'href',
    `/${scanner ? 'manifest-scanner' : 'manifest'}${
      prod ? '' : '-staging'
    }.webmanifest`,
  );
  set(
    'link[rel="apple-touch-icon"]',
    'href',
    `/icons/apple-touch-icon-180${prod ? '' : '-staging'}.png`,
  );
  set(
    'meta[name="apple-mobile-web-app-title"]',
    'content',
    `${scanner ? 'Odeko Scanner' : 'Odeko Warehouse'}${
      prod ? '' : ' (Staging)'
    }`,
  );
};

// Record, as early as possible in startup, that this session was launched as an
// installed PWA. The Auth0 login redirect can land the callback in a context
// where the display-mode media query no longer reports standalone, so we persist
// the signal to recover PWA-specific behavior (post-login destination, hiding
// the "use Chrome" nudge) for the rest of the session. The Scanner app has no
// login and nothing to recover, so a launch there clears the mark instead. A
// mark would lock DMP users in Chrome on that handheld to Putaway, and handhelds
// that installed Putaway from /scanner before the Scanner app existed still
// carry one: deleting an installed app doesn't clear the origin's storage.
export const recordPwaLaunch = (): void => {
  if (!isStandaloneDisplay()) return;
  try {
    if (isScannerPage(window.location.pathname)) {
      window.localStorage.removeItem(PWA_FLAG);
    } else {
      window.localStorage.setItem(PWA_FLAG, '1');
    }
  } catch {
    // Storage can throw in private modes; the live display check still works.
  }
};

// Record the launch now, and again whenever the page comes back to the front.
// Putaway and the Scanner app share one origin's storage, so on a shared
// handheld opening the Scanner app clears Putaway's mark while Putaway keeps
// running. Re-recording on return puts the mark back before a login redirect.
// Returns a function that stops watching.
export const watchPwaLaunch = (): (() => void) => {
  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') recordPwaLaunch();
  };
  recordPwaLaunch();
  document.addEventListener('visibilitychange', onVisibilityChange);
  return () =>
    document.removeEventListener('visibilitychange', onVisibilityChange);
};

// True when the app is running as an installed PWA — either right now, or earlier
// this session (persisted across the Auth0 redirect).
export const isPwaContext = (): boolean => {
  try {
    if (window.localStorage.getItem(PWA_FLAG) === '1') return true;
  } catch {
    // Ignore and fall back to the live display check.
  }
  return isStandaloneDisplay();
};
